/**
 * 勤怠修正の未送信キュー（圏外対応）。
 *
 * 沖では圏外が普通なので、勤怠の修正・追加・削除を圏外でも受け付け、端末に貯めておいて
 * 電波が入ったら自動で送る。打刻のキュー（syncQueue.ts）は打刻専用の形なので、
 * 勤怠修正はこちらで持つ。
 *
 * 表示は「サーバーの内容 ＋ 未送信ぶんを上から重ねる」方式にしている（applyPendingEdits）。
 * 端末のキャッシュ自体は書き換えないので、送信が済めばサーバーの内容にそのまま戻る。
 */

export type EditKind = 'update' | 'create' | 'delete';

export interface PendingEdit {
  /** キューのID */
  id: string;
  kind: EditKind;
  /** 対象の勤務日 YYYY-MM-DD */
  target_date: string;
  /** update / delete の対象セッションID。create のときは端末内の仮ID（local-…） */
  session_id: string;
  /** サーバーへ送る中身（delete は不要） */
  body?: Record<string, unknown>;
  /** 画面に出す変更後の値（重ね表示用） */
  view?: { clockIn?: string; clockOut?: string; breakMin?: number; workType?: string };
  queued_at: string;
  attempts: number;
}

const KEY = 'catms_pending_edits';
const MAX_ATTEMPTS = 5;

export function getEdits(): PendingEdit[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as PendingEdit[]; }
  catch { return []; }
}

function save(list: PendingEdit[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* 容量超過は無視 */ }
}

export function countEdits(): number { return getEdits().length; }

/** その月（YYYY-MM）の未送信ぶん */
export function getEditsForMonth(ym: string): PendingEdit[] {
  return getEdits().filter(e => e.target_date.startsWith(ym));
}

export function enqueueEdit(e: Omit<PendingEdit, 'id' | 'queued_at' | 'attempts'>): PendingEdit {
  const entry: PendingEdit = { ...e, id: crypto.randomUUID(), queued_at: new Date().toISOString(), attempts: 0 };
  save([...getEdits(), entry]);
  return entry;
}

export function removeEdit(id: string): void {
  save(getEdits().filter(e => e.id !== id));
}

/** まだ送っていない「追加」を取り消す（端末内の仮IDで消す） */
export function removeEditsBySession(sessionId: string): void {
  save(getEdits().filter(e => e.session_id !== sessionId));
}

/** 端末内だけに存在する（まだ送っていない）追加行か */
export function isLocalId(id: string): boolean { return id.startsWith('local-'); }

export function newLocalId(): string { return `local-${crypto.randomUUID()}`; }

/** 通信できないことが原因の失敗か（サーバーが返した業務エラーと区別する） */
export function isOfflineError(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const msg = e instanceof Error ? e.message : String(e ?? '');
  return /Failed to fetch|NetworkError|Load failed|ネットワーク|通信/i.test(msg);
}

type ApiFetch = (path: string, opts?: RequestInit) => Promise<unknown>;

/**
 * 貯めた修正をサーバーへ送る。
 * - 4xx（対象が消えている等）は送り直しても直らないのでキューから捨てる
 * - 通信エラー / 5xx は次回に持ち越して打ち切る
 */
let _flushing = false;

export async function flushEdits(apiFetch: ApiFetch): Promise<{ synced: number; failed: number }> {
  // 画面表示時と online イベントが同時に走ると同じ修正を2回送ってしまう
  // （「追加」なら勤怠が二重に登録される）ので、実行中は後から来たぶんを捨てる
  if (_flushing) return { synced: 0, failed: 0 };
  const list = getEdits();
  if (!list.length) return { synced: 0, failed: 0 };
  _flushing = true;
  let synced = 0, failed = 0;
  try {
  for (const e of list) {
    if (e.attempts >= MAX_ATTEMPTS) { removeEdit(e.id); continue; }
    try {
      if (e.kind === 'update') {
        await apiFetch(`/attendance/${e.session_id}`, { method: 'PUT', body: JSON.stringify(e.body ?? {}) });
      } else if (e.kind === 'delete') {
        await apiFetch(`/attendance/${e.session_id}`, { method: 'DELETE' });
      } else {
        await apiFetch('/attendance', { method: 'POST', body: JSON.stringify(e.body ?? {}) });
      }
      removeEdit(e.id);
      synced++;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const is4xx = /BAD_REQUEST|NOT_FOUND|見つかりません|HTTP 4/.test(msg);
      if (is4xx) { removeEdit(e.id); continue; }
      save(getEdits().map(x => x.id === e.id ? { ...x, attempts: x.attempts + 1 } : x));
      failed++;
      break;   // 通信できていないので以降も失敗する。次の機会に回す
    }
  }
  } finally { _flushing = false; }
  return { synced, failed };
}
